import { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * RenameMeeting — pencil affordance that swaps the session title for an inline
 * input. PATCHes `/meetings/{id}` with the new title and hands the updated
 * meeting back through `onUpdated`.
 */
export default function RenameMeeting({ meeting, onUpdated, compact = false, iconOnly = false, onEditingChange }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(meeting?.title || "");
  const [saving, setSaving] = useState(false);
  const [applyToSeries, setApplyToSeries] = useState(false);
  const inSeries = !!meeting?.series_id;

  const setEditingState = (v) => {
    setEditing(v);
    onEditingChange && onEditingChange(v);
  };

  // Series titles carry a "· n/total" suffix — strip it when editing the set.
  const baseTitle = (t) => (t || "").replace(/\s·\s\d+\/\d+$/, "");

  const save = async () => {
    const title = value.trim();
    if (!title || title === meeting.title) {
      setEditingState(false);
      setValue(meeting.title || "");
      return;
    }
    setSaving(true);
    try {
      if (inSeries && applyToSeries) {
        const r = await api.patch(`/meetings/series/${meeting.series_id}`, {
          from_meeting_id: meeting.id,
          title: baseTitle(title),
        });
        toast.success(`Renamed ${r.data.updated} remaining session${r.data.updated === 1 ? "" : "s"}.`);
        onUpdated && onUpdated({ ...meeting, title }, { refetch: true });
      } else {
        await api.patch(`/meetings/${meeting.id}`, { title });
        onUpdated && onUpdated({ ...meeting, title });
        toast.success("Session renamed.");
      }
      setEditingState(false);
    } catch {
      toast.error("Could not rename the session.");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-1 flex-1 min-w-0 sm:flex-none sm:w-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1.5 w-full">
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") { setEditingState(false); setValue(meeting.title || ""); }
          }}
          className={`bg-[#121212] border border-[#c89f45]/50 rounded-md px-2 py-1 min-h-[40px] sm:min-h-0 text-[#eaeaea] ${compact ? "text-sm flex-1 min-w-0 sm:w-48 sm:flex-none" : "text-base flex-1 min-w-0 sm:w-64 sm:flex-none"}`}
          data-testid={`rename-input-${meeting.id}`}
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="h-10 w-10 sm:h-8 sm:w-8 shrink-0 rounded-md text-[#c89f45] hover:bg-[#1a1a1a] flex items-center justify-center disabled:opacity-40"
          aria-label="Save title"
          data-testid={`rename-save-${meeting.id}`}
        >
          <Check size={15} />
        </button>
        <button
          type="button"
          onClick={() => { setEditingState(false); setValue(meeting.title || ""); }}
          className="h-10 w-10 sm:h-8 sm:w-8 shrink-0 rounded-md text-[#666] hover:text-[#eaeaea] hover:bg-[#1a1a1a] flex items-center justify-center"
          aria-label="Cancel rename"
          data-testid={`rename-cancel-${meeting.id}`}
        >
          <X size={15} />
        </button>
        </div>
        {inSeries && (
          <label className="flex items-center gap-1.5 text-[10px] font-mono text-[#8a8a8a] cursor-pointer pl-0.5">
            <input
              type="checkbox"
              checked={applyToSeries}
              onChange={(e) => setApplyToSeries(e.target.checked)}
              className="accent-[#c89f45]"
              data-testid={`rename-apply-series-${meeting.id}`}
            />
            Apply to the rest of the series
          </label>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingState(true); }}
      className={`rounded-md text-[#666] hover:text-[#c89f45] hover:bg-[#1a1a1a] flex items-center justify-center transition-colors shrink-0 ${
        compact ? "h-10 w-10 sm:h-8 sm:w-8" : "h-11 w-11 sm:h-9 sm:w-9"
      }`}
      aria-label="Rename session"
      title="Rename session"
      data-testid={`rename-trigger-${meeting.id}`}
    >
      <Pencil size={compact ? 13 : 15} />
      {!iconOnly && <span className="sr-only">Rename</span>}
    </button>
  );
}
